/**
 * @file MiniRouter.tsx
 * @author ickeep
 * @description 轻量级路由容器组件，提供路由上下文和导航功能
 * @since 0.0.1
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Location, MiniRouterContext } from '../context/MiniRouterContext';

/**
 * MiniRouter 组件的属性接口
 * @interface MiniRouterProps
 * @property {React.ReactNode} children - 子组件
 * @property {string} [basename] - 路由基础路径
 */
interface MiniRouterProps {
  children: React.ReactNode;
  basename?: string;
}

/**
 * 轻量级路由容器组件
 * @component
 * @summary 提供路由上下文和导航功能，管理路由状态和导航操作
 * @param {MiniRouterProps} props - 组件属性
 * @returns {JSX.Element} 路由容器组件
 * @example
 * ```tsx
 * <MiniRouter basename="/app">
 *   <MiniRoute path="/" component={<Home />} />
 *   <MiniRoute path="/about" component={<About />} />
 * </MiniRouter>
 * ```
 */
/**
 * 解析 URL 获取 location 对象
 * @param {string} url - 完整 URL 或路径
 * @param {unknown} state - 状态数据
 * @param {string} key - 唯一键值
 * @returns {Location} location 对象
 */

const parseLocation = (url: string, state: unknown = null, key = ''): Location => {
  try {
    const baseUrl = window.location?.origin || 'http://localhost';
    const urlObj = new URL(url, baseUrl);
    return {
      pathname: urlObj.pathname,
      search: urlObj.search,
      hash: urlObj.hash,
      state,
      key: key || `${Date.now()}-${Math.random()}`,
    };
  } catch {
    // 如果 URL 解析失败，尝试手动解析
    const [pathnameWithSearch, hash = ''] = url.split('#');
    const [pathname, search = ''] = pathnameWithSearch.split('?');
    return {
      pathname: pathname || '/',
      search: search ? `?${search}` : '',
      hash: hash ? `#${hash}` : '',
      state,
      key: key || `${Date.now()}-${Math.random()}`,
    };
  }
};

/**
 * 从查询字符串解析参数
 * @param {string} search - 查询字符串
 * @returns {Record<string, string | undefined>} 参数对象
 */
const parseSearchParams = (search: string): Record<string, string | undefined> => {
  const params: Record<string, string | undefined> = {};
  if (search) {
    const searchParams = new URLSearchParams(search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
  }
  return params;
};

/**
 * 从路径模式中提取参数
 * @param {string} pattern - 路径模式（如 /user/:id）
 * @param {string} pathname - 实际路径（如 /user/123）
 * @returns {Record<string, string | undefined>} 参数对象
 */
const extractPathParams = (pattern: string, pathname: string): Record<string, string | undefined> => {
  const params: Record<string, string | undefined> = {};
  const patternParts = pattern.split('/');
  const pathParts = pathname.split('/');

  if (patternParts.length !== pathParts.length) {
    return params;
  }

  patternParts.forEach((part, index) => {
    if (part.startsWith(':')) {
      const paramName = part.slice(1);
      params[paramName] = pathParts[index];
    }
  });

  return params;
};

export const MiniRouter: React.FC<MiniRouterProps> = ({ children, basename }) => {
  // 初始化 location
  const initialLocation = useMemo(() => parseLocation(window.location.href), []);

  // 当前路径状态
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  // Location 状态
  const [location, setLocation] = useState<Location>(initialLocation);
  // 已注册路由集合
  const [routes, setRoutes] = useState<Set<string>>(new Set());

  /**
   * 更新 location 和路径
   * @param {string} url - 目标 URL
   * @param {unknown} state - 状态数据
   * @param {boolean} replace - 是否替换历史记录
   */
  const updateLocation = useCallback((url: string, state: unknown = null, replace = false) => {
    const newLocation = parseLocation(url, state);
    const { pathname } = newLocation;

    if (replace) {
      window.history.replaceState(state, '', url);
    } else {
      window.history.pushState(state, '', url);
    }

    setLocation(newLocation);
    setCurrentPath(pathname);
  }, []);

  /**
   * 导航函数（兼容旧版本）
   * @param {string} path - 目标路径
   */
  const navigate = useCallback((path: string) => {
    // 处理 basename
    let fullPath = path;
    if (basename) {
      // 如果 path 已经包含 basename，则不重复添加
      if (path.startsWith(basename)) {
        fullPath = path;
      } else {
        fullPath = `${basename}${path.startsWith('/') ? path : `/${path}`}`;
      }
    }
    updateLocation(fullPath, null, false);
  }, [basename, updateLocation]);

  /**
   * 检查路径是否匹配
   * @param {string} path - 待检查的路径
   * @returns {boolean} 是否匹配
   */
  const hasMatch = useCallback((path: string): boolean => {
    if (path === '*') return true;
    if (path.endsWith('*')) {
      const basePath = path.slice(0, -2);
      return currentPath.startsWith(basePath);
    }
    // 支持参数化路径匹配（如 /user/:id）
    if (path.includes(':')) {
      const patternParts = path.split('/');
      const pathParts = currentPath.split('/');
      if (patternParts.length !== pathParts.length) {
        return false;
      }
      return patternParts.every((part, index) => {
        // 如果是参数（以 : 开头），则匹配任何值
        if (part.startsWith(':')) {
          return true;
        }
        // 否则必须精确匹配
        return part === pathParts[index];
      });
    }
    return currentPath === path;
  }, [currentPath]);

  /**
   * 获取最佳匹配路由
   * @returns {string | null} 最佳匹配的路由路径
   */
  const getBestMatchRoute = useCallback((): string | null => {
    let best: string | null = null;
    let bestLength = -1;
    routes.forEach((routePath) => {
      if (hasMatch(routePath)) {
        if (routePath === '*') {
          if (best === null) {
            best = routePath;
            bestLength = 0;
          }
        } else {
          const depth = routePath === '*' ? 0 : routePath.replace(/\*$/, '').length;
          if (depth > bestLength) {
            best = routePath;
            bestLength = depth;
          }
        }
      }
    });
    return best;
  }, [routes, hasMatch]);

  /**
   * 解析路由参数
   * @returns {Readonly<Record<string, string | undefined>>} 路由参数对象（只读）
   */
  const routeParams = useMemo(() => {
    const bestMatch = getBestMatchRoute();
    let params: Record<string, string | undefined>;

    if (!bestMatch) {
      params = parseSearchParams(location.search);
    } else {
      // 从路径模式中提取参数
      const pathParams = extractPathParams(bestMatch, location.pathname);
      // 从查询字符串中提取参数
      const searchParams = parseSearchParams(location.search);

      // 合并参数，查询字符串优先级更高
      params = { ...pathParams, ...searchParams };
    }

    // 返回只读对象以符合 IUseRouteParams 接口要求
    return Object.freeze(params) as Readonly<Record<string, string | undefined>>;
  }, [location.pathname, location.search, getBestMatchRoute]);

  /**
   * 注册路由
   * @param {string} path - 路由路径
   */
  const registerRoute = useCallback((path: string) => {
    setRoutes((prev) => {
      if (prev.has(path)) return prev;
      return new Set([...prev, path]);
    });
  }, []);

  /**
   * 注销路由
   * @param {string} path - 路由路径
   */
  const unregisterRoute = useCallback((path: string) => {
    setRoutes((prev) => {
      if (!prev.has(path)) return prev;
      const newRoutes = new Set(prev);
      newRoutes.delete(path);
      return newRoutes;
    });
  }, []);

  // 监听浏览器历史记录变化
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const newLocation = parseLocation(window.location.href, event.state);
      setLocation(newLocation);
      setCurrentPath(newLocation.pathname);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // 路由上下文值
  const contextValue = useMemo(() => ({
    currentPath,
    location,
    routeParams,
    navigate,
    updateLocation,
    hasMatch,
    registerRoute,
    unregisterRoute,
    routes,
    getBestMatchRoute,
  }), [currentPath, location, routeParams, navigate, updateLocation, hasMatch, registerRoute, unregisterRoute, routes, getBestMatchRoute]);

  return (
    <MiniRouterContext.Provider value={contextValue}>
      {children}
    </MiniRouterContext.Provider>
  );
};
